import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";
import { Loader2, PiggyBank, FileDown, AlertTriangle } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatMonthYear } from "@/lib/format";
import { WarningCreateDialog } from "@/components/WarningsPanel";

export const fmtMin = (m) => {
  if (m == null) return "—";
  const sign = m < 0 ? "-" : "";
  const a = Math.abs(Math.round(m));
  return `${sign}${Math.floor(a / 60)}h${String(a % 60).padStart(2, "0")}`;
};

const monthNow = () => new Date().toLocaleDateString("sv-SE").slice(0, 7);
const WEEKDAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

const Stat = ({ label, value, tone = "", testId }) => (
  <div className="text-center">
    <div className="text-[10px] uppercase tracking-wide text-stone-500 font-body">{label}</div>
    <div className={`font-display font-bold ${tone}`} data-testid={testId}>
      {value}
    </div>
  </div>
);

export default function TimesheetTab() {
  const [month, setMonth] = useState(monthNow());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [warnOpen, setWarnOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/timeclock/timesheet", { params: { month } });
      setData(data);
    } catch (err) {
      console.error("timesheet load failed", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [month]);

  const openDetail = async (row) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setDetail(null);
    try {
      const { data } = await api.get("/timeclock/timesheet", {
        params: { month, user_id: row.user_id },
      });
      setDetail(data);
    } catch (err) {
      console.error("timesheet detail failed", err);
    } finally {
      setDetailLoading(false);
    }
  };

  const download = async (kind) => {
    try {
      const resp = await api.get(`/timeclock/export/${kind}`, {
        params: { month },
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${kind.toUpperCase()}_${month}.txt`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Arquivo ${kind.toUpperCase()} gerado`);
    } catch (err) {
      toast.error("Falha ao gerar arquivo");
    }
  };

  const rows = data?.rows || [];

  const downloadPdf = async (userId, userName) => {
    try {
      const resp = await api.get("/timeclock/timesheet/pdf", {
        params: { month, user_id: userId },
        responseType: "blob",
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `espelho_${month}_${(userName || "colaborador").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF do fechamento gerado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao gerar PDF");
    }
  };

  return (
    <div className="space-y-4" data-testid="timesheet-tab">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs">Mês de apuração</Label>
          <Input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="mt-1 bg-white w-44"
            data-testid="timesheet-month-filter"
          />
        </div>
        {loading && <Loader2 className="h-4 w-4 animate-spin text-stone-400 mb-2.5" />}
        <div className="flex-1" />
        <Button
          variant="outline"
          size="sm"
          className="bg-white"
          onClick={() => download("afd")}
          data-testid="timesheet-export-afd"
        >
          <FileDown className="h-4 w-4 mr-1.5" /> AFD (Portaria 671)
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="bg-white"
          onClick={() => download("aej")}
          data-testid="timesheet-export-aej"
        >
          <FileDown className="h-4 w-4 mr-1.5" /> AEJ (Portaria 671)
        </Button>
      </div>

      <Card className="rounded-xl border-stone-200 bg-white overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-stone-50">
              <TableHead className="font-body">Colaborador</TableHead>
              <TableHead className="font-body">Dias</TableHead>
              <TableHead className="font-body">Trabalhado</TableHead>
              <TableHead className="font-body">Previsto</TableHead>
              <TableHead className="font-body">Extra 50%</TableHead>
              <TableHead className="font-body">Extra 100%</TableHead>
              <TableHead className="font-body">Ad. noturno</TableHead>
              <TableHead className="font-body">Faltas</TableHead>
              <TableHead className="font-body">Saldo mês</TableHead>
              <TableHead className="font-body">Política</TableHead>
              <TableHead className="font-body">Espelho</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="timesheet-table">
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={11} className="text-center text-stone-500 py-10">
                  Nenhum colaborador com jornada ou registros neste mês.
                </TableCell>
              </TableRow>
            )}
            {rows.map((r) => (
              <TableRow
                key={r.user_id}
                className="cursor-pointer hover:bg-stone-50"
                onClick={() => openDetail(r)}
                data-testid={`timesheet-row-${r.user_id}`}
              >
                <TableCell className="font-display font-semibold text-sm">
                  {r.name}
                  {!r.has_schedule && (
                    <Badge variant="outline" className="ml-2 text-[9px] bg-stone-100 text-stone-500 border-stone-200">
                      sem jornada
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="font-body text-sm tabular-nums">{r.totals.days_worked}</TableCell>
                <TableCell className="font-body text-sm tabular-nums">{fmtMin(r.totals.worked_effective)}</TableCell>
                <TableCell className="font-body text-sm tabular-nums">{fmtMin(r.totals.expected_minutes)}</TableCell>
                <TableCell className="font-body text-sm tabular-nums">{fmtMin(r.totals.overtime_50)}</TableCell>
                <TableCell className="font-body text-sm tabular-nums">{fmtMin(r.totals.overtime_100)}</TableCell>
                <TableCell className="font-body text-sm tabular-nums">{fmtMin(r.totals.night_additional_minutes)}</TableCell>
                <TableCell className="font-body text-sm tabular-nums">
                  {r.totals.absences > 0 ? (
                    <span className="text-rose-700 font-semibold">{r.totals.absences}</span>
                  ) : (
                    0
                  )}
                </TableCell>
                <TableCell
                  className={`font-display font-bold text-sm tabular-nums ${
                    r.totals.balance_minutes < 0 ? "text-rose-700" : "text-emerald-800"
                  }`}
                >
                  {fmtMin(r.totals.balance_minutes)}
                </TableCell>
                <TableCell>
                  <Badge
                    variant="outline"
                    className={`text-[10px] ${
                      r.policy === "payroll"
                        ? "bg-sky-50 text-sky-800 border-sky-200"
                        : "bg-emerald-50 text-emerald-800 border-emerald-200"
                    }`}
                  >
                    {r.policy === "payroll" ? "Folha" : "Banco de horas"}
                  </Badge>
                </TableCell>
                <TableCell data-testid={`timesheet-signed-${r.user_id}`}>
                  {r.signed ? (
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200 text-[10px]">
                      Assinado
                    </Badge>
                  ) : (
                    <span className="text-xs text-stone-400 font-body">—</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog
        open={detailOpen}
        onOpenChange={(o) => {
          setDetailOpen(o);
          if (!o) setDetail(null);
        }}
      >
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="timesheet-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              Apuração — {detail?.user?.name || "…"} · {formatMonthYear(month)}
            </DialogTitle>
            <DialogDescription className="font-body text-xs">
              Detalhamento diário de horas, extras, noturno e banco de horas.
            </DialogDescription>
          </DialogHeader>
          {detailLoading && (
            <div className="py-8 text-center">
              <Loader2 className="h-5 w-5 animate-spin mx-auto text-stone-400" />
            </div>
          )}
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 bg-stone-50 rounded-lg p-3">
                <Stat label="Trabalhado" value={fmtMin(detail.totals.worked_effective)} />
                <Stat label="Previsto" value={fmtMin(detail.totals.expected_minutes)} />
                <Stat label="Extra 50%" value={fmtMin(detail.totals.overtime_50)} />
                <Stat label="Extra 100%" value={fmtMin(detail.totals.overtime_100)} />
                <Stat label="Ad. noturno" value={fmtMin(detail.totals.night_additional_minutes)} />
                <Stat
                  label="Saldo mês"
                  value={fmtMin(detail.totals.balance_minutes)}
                  tone={detail.totals.balance_minutes < 0 ? "text-rose-700" : "text-emerald-800"}
                  testId="timesheet-detail-balance"
                />
              </div>
              {detail.bank_balance != null && (
                <div
                  className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-body"
                  data-testid="timesheet-detail-bank"
                >
                  <PiggyBank className="h-4 w-4 text-emerald-700" />
                  Banco de horas acumulado:{" "}
                  <span
                    className={`font-display font-bold ${
                      detail.bank_balance < 0 ? "text-rose-700" : "text-emerald-800"
                    }`}
                  >
                    {fmtMin(detail.bank_balance)}
                  </span>
                </div>
              )}
              {detail.signature && (
                <div
                  className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-body"
                  data-testid="timesheet-detail-signature"
                >
                  ✍ Espelho assinado pelo colaborador em{" "}
                  {new Date(detail.signature.signed_at).toLocaleString("pt-BR", {
                    dateStyle: "short",
                    timeStyle: "short",
                  })}{" "}
                  · IP {detail.signature.ip_address || "—"} ·{" "}
                  <span className="font-mono" title={detail.signature.hash}>
                    hash {String(detail.signature.hash).slice(0, 16)}…
                  </span>
                </div>
              )}
              {detail.policy === "payroll" && (
                <p className="text-xs text-stone-500 font-body">
                  Política desta jornada: extras contabilizadas para pagamento em folha (sem banco de horas).
                </p>
              )}
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50">
                    <TableHead className="font-body">Data</TableHead>
                    <TableHead className="font-body">Batidas</TableHead>
                    <TableHead className="font-body">Trabalhado</TableHead>
                    <TableHead className="font-body">Previsto</TableHead>
                    <TableHead className="font-body">Noturno</TableHead>
                    <TableHead className="font-body">Saldo</TableHead>
                    <TableHead className="font-body">Situação</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody data-testid="timesheet-detail-days">
                  {detail.days.map((d) => (
                    <TableRow key={d.date} className={d.absent ? "bg-rose-50/40" : ""}>
                      <TableCell className="font-body text-sm tabular-nums">
                        {d.date.slice(8)}/{d.date.slice(5, 7)}{" "}
                        <span className="text-stone-400 text-xs">{WEEKDAYS[d.weekday]}</span>
                      </TableCell>
                      <TableCell className="font-body text-xs tabular-nums">
                        {["entry", "break_start", "break_end", "exit"]
                          .map((t) => d.punches[t])
                          .filter(Boolean)
                          .join(" · ") || "—"}
                      </TableCell>
                      <TableCell className="font-body text-sm tabular-nums">
                        {d.worked_minutes ? fmtMin(d.worked_effective) : "—"}
                      </TableCell>
                      <TableCell className="font-body text-sm tabular-nums">
                        {d.expected_minutes ? fmtMin(d.expected_minutes) : "—"}
                      </TableCell>
                      <TableCell className="font-body text-sm tabular-nums">
                        {d.night_minutes ? fmtMin(d.night_minutes) : "—"}
                      </TableCell>
                      <TableCell
                        className={`font-body text-sm font-semibold tabular-nums ${
                          d.balance_minutes < 0
                            ? "text-rose-700"
                            : d.balance_minutes > 0
                            ? "text-emerald-800"
                            : "text-stone-400"
                        }`}
                      >
                        {d.workday || d.worked_minutes ? fmtMin(d.balance_minutes) : "—"}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {d.is_holiday && (
                            <Badge variant="outline" className="bg-sky-50 text-sky-800 border-sky-200 text-[9px]">
                              Feriado{d.holiday_name ? ` · ${d.holiday_name}` : ""}
                            </Badge>
                          )}
                          {d.leave_type && (
                            <Badge variant="outline" className="bg-indigo-50 text-indigo-800 border-indigo-200 text-[9px]">
                              {{
                                vacation: "Férias",
                                medical: "Atestado",
                                license: "Licença",
                                comp_day: "Folga comp.",
                                other: "Afastamento",
                              }[d.leave_type] || "Afastamento"}
                            </Badge>
                          )}
                          {d.absent && (
                            <Badge variant="outline" className="bg-rose-50 text-rose-800 border-rose-200 text-[9px]">
                              Falta
                            </Badge>
                          )}
                          {d.records_count > 0 && !d.complete && (
                            <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-[9px]">
                              Incompleto
                            </Badge>
                          )}
                          {!d.workday && !d.is_holiday && d.records_count === 0 && (
                            <span className="text-[10px] text-stone-400 font-body">folga</span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {detail && (
            <div className="flex flex-wrap justify-end gap-2 pt-2">
              {(detail.totals.absences > 0 || detail.totals.balance_minutes < 0) && (
                <Button
                  size="sm"
                  variant="outline"
                  className="border-rose-300 text-rose-700 bg-white hover:bg-rose-50"
                  onClick={() => setWarnOpen(true)}
                  data-testid="timesheet-warning-btn"
                >
                  <AlertTriangle className="h-4 w-4 mr-1.5" /> Gerar advertência
                </Button>
              )}
              <Button
                size="sm"
                className="bg-emerald-800 hover:bg-emerald-900 text-white"
                onClick={() => downloadPdf(detail.user?.id, detail.user?.name)}
                data-testid="timesheet-pdf-btn"
              >
                <FileDown className="h-4 w-4 mr-1.5" /> Baixar PDF do fechamento
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {detail && (
        <WarningCreateDialog
          open={warnOpen}
          onOpenChange={setWarnOpen}
          userId={detail.user?.id}
          userName={detail.user?.name}
          month={month}
          defaultDetails={`Faltas no mês: ${detail.totals.absences}; Saldo do mês: ${fmtMin(
            detail.totals.balance_minutes
          )}; Dias com falta: ${detail.days
            .filter((d) => d.absent)
            .map((d) => d.date.split("-").reverse().join("/"))
            .join(", ") || "—"}`}
          onCreated={() => toast.info("Consulte a aba Advertências para colher a assinatura")}
        />
      )}
    </div>
  );
}
